import React, { useEffect, useState, useRef } from 'react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import {
  triggerFullSync,
  getForecastJobStatus,
  getFeeForecastData,
  getSummary,
  triggerBulkManualForecast,
} from '../../../redux/features/feeForecastSlice';
import dayjs from 'dayjs';

const ManualTriggers = ({ date, dispatchParams }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const handleApiResponse = useHandleApiResponse();
  const { jobStatus } = useSelector((state) => state.feeForecast);

  const jobStatusValue = jobStatus?.fullSync?.status;
  const manualJobStatusValue = jobStatus?.manualForecast?.status;

  // Fetch status once when modal opens
  useEffect(() => {
    if (open) {
      dispatch(getForecastJobStatus());
    }
  }, [open, dispatch]);

  // Manage polling interval based on job statuses
  useEffect(() => {
    if (open && (jobStatusValue === 'RUNNING' || manualJobStatusValue === 'RUNNING')) {
      const interval = setInterval(() => {
        dispatch(getForecastJobStatus());
      }, 2000); // Poll every 2 seconds
      return () => clearInterval(interval);
    }
  }, [open, jobStatusValue, manualJobStatusValue, dispatch]);

  const selectedMonth = dayjs(date).month() + 1;
  const selectedYear = dayjs(date).year();

  // Full Sync Job (Phase 1 & Phase 2)
  const rawJob = jobStatus?.fullSync;
  const isCurrentMonthJob = rawJob && rawJob.month === selectedMonth && rawJob.year === selectedYear;

  const job = isCurrentMonthJob ? rawJob : {
    status: 'IDLE',
    currentPhase: 'IDLE',
    expectedSessions: { total: 0, processed: 0, errors: 0 },
    feeCalculation: { total: 0, processed: 0, errors: 0 },
    errorList: [],
    estimatedSecondsRemaining: 0
  };

  // Manual Sync Job (Phase 3)
  const rawManualJob = jobStatus?.manualForecast;
  const isCurrentMonthManualJob = rawManualJob && rawManualJob.month === selectedMonth && rawManualJob.year === selectedYear;

  const manualJob = isCurrentMonthManualJob ? rawManualJob : {
    status: 'IDLE',
    total: 0,
    processed: 0,
    errors: 0,
    errorList: [],
    estimatedSecondsRemaining: 0
  };

  const isRunning = job.status === 'RUNNING';
  const isManualRunning = manualJob.status === 'RUNNING';
  const isAnyRunning = isRunning || isManualRunning;

  const prevStatusRef = useRef(null);
  const prevManualStatusRef = useRef(null);

  // Full sync completion handler -> starts Phase 3 automatically
  useEffect(() => {
    if (isCurrentMonthJob && rawJob) {
      const currentStatus = rawJob.status;
      if (prevStatusRef.current === 'RUNNING' && currentStatus === 'COMPLETED') {
        enqueueSnackbar('Phase 1 & 2 Completed! Starting Phase 3: Manual Forecast Sync...', { variant: 'info' });
        
        // Trigger Phase 3 (Manual Forecast Sync)
        const formattedDate = dayjs(date).format('YYYY-MM-DD');
        dispatch(triggerBulkManualForecast({ date: formattedDate })).then((res) => {
          handleApiResponse(res, {
            onSuccess: () => {
              dispatch(getForecastJobStatus());
            },
          });
        });
      }
      prevStatusRef.current = currentStatus;
    }
  }, [rawJob, isCurrentMonthJob, dispatch, date, handleApiResponse, enqueueSnackbar]);

  // Manual forecast completion handler
  useEffect(() => {
    if (isCurrentMonthManualJob && rawManualJob) {
      const currentStatus = rawManualJob.status;
      if (prevManualStatusRef.current === 'RUNNING' && currentStatus === 'COMPLETED') {
        enqueueSnackbar('Forecast Calculations Sync Completed Successfully!', { variant: 'success' });
        dispatch(getFeeForecastData(dispatchParams));
        dispatch(getSummary(dispatchParams));
      }
      prevManualStatusRef.current = currentStatus;
    }
  }, [rawManualJob, isCurrentMonthManualJob, dispatch, dispatchParams, enqueueSnackbar]);

  const handleStartSync = () => {
    if (job.status === 'COMPLETED' || manualJob.status === 'COMPLETED') {
      const confirmRun = window.confirm(
        `A calculations sync has already been completed for ${dayjs(date).format('MMMM YYYY')}. Re-running it will recalculate all expected sessions and fees. Are you sure you want to proceed?`
      );
      if (!confirmRun) return;
    }
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    dispatch(triggerFullSync({ date: formattedDate })).then((res) => {
      handleApiResponse(res, {
        onSuccess: () => {
          enqueueSnackbar('Forecast calculation sync started (Phase 1/3)', { variant: 'info' });
          dispatch(getForecastJobStatus());
        },
      });
    });
  };

  const getProgress = (processed, total) => {
    if (!total) return 0;
    return Math.min(100, Math.round((processed / total) * 100));
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const expectedProgress = getProgress(job?.expectedSessions?.processed, job?.expectedSessions?.total);
  const feeProgress = getProgress(job?.feeCalculation?.processed, job?.feeCalculation?.total);
  const manualProgress = getProgress(manualJob?.processed, manualJob?.total);

  const getOverallStatus = () => {
    if (isRunning) {
      if (job.currentPhase === 'FINALIZING_EXPECTED_SESSIONS') return 'RUNNING (Phase 1/3)';
      if (job.currentPhase === 'CALCULATING_FEES') return 'RUNNING (Phase 2/3)';
      return 'RUNNING';
    }
    if (isManualRunning) return 'RUNNING (Phase 3/3)';
    if (job?.status === 'FAILED' || manualJob?.status === 'FAILED') return 'FAILED';
    if (manualJob?.status === 'COMPLETED') return 'COMPLETED';
    if (job?.status === 'COMPLETED') return 'RUNNING (Transitioning to Phase 3)';
    return 'IDLE';
  };

  const overallStatus = getOverallStatus();

  const getStatusColor = (status) => {
    if (status.startsWith('RUNNING')) return 'info.main';
    if (status === 'FAILED') return 'error.main';
    if (status === 'COMPLETED') return 'success.main';
    return 'text.secondary';
  };

  const activeSecondsRemaining = isRunning
    ? job?.estimatedSecondsRemaining
    : (isManualRunning ? manualJob?.estimatedSecondsRemaining : 0);

  return (
    <GlobalDialog
      open={open}
      setOpen={setOpen}
      title={'Manual Calculations Sync'}
      closeButton={true}
      maxWidth={'sm'}
      fullWidth
      popupButton={
        <Button
          startIcon={<Icon icon="mdi:sync-circle" width="24" height="24" />}
          size="small"
          variant="contained"
          color="success"
          onClick={() => setOpen(true)}
        >
          Manual Trigger
        </Button>
      }
      content={
        <Box sx={{ mt: 1 }}>
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <strong>Warning:</strong> This process is resource-heavy. To prevent database performance issues, please run this sync <strong>only during low user traffic/off-peak hours</strong>.
          </Alert>

          {/* ── SYNC STATUS CARD ── */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="700">
                  Calculations Sync Status
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Status:{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight="700"
                    sx={{ color: getStatusColor(overallStatus) }}
                  >
                    {overallStatus}
                  </Typography>
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleStartSync}
                disabled={isAnyRunning}
                startIcon={isAnyRunning ? <Icon icon="line-md:loading-loop" /> : <Icon icon="mdi:sync" />}
              >
                {isAnyRunning ? 'Syncing...' : 'Start Sync'}
              </Button>
            </Box>

            {isAnyRunning && activeSecondsRemaining > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Estimated remaining time: <strong>{formatTimeRemaining(activeSecondsRemaining)}</strong>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* PHASE 1 */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color={job?.currentPhase === 'FINALIZING_EXPECTED_SESSIONS' ? 'primary.main' : 'text.primary'}
                >
                  Phase 1: Finalize Expected Sessions
                </Typography>
                {job?.expectedSessions?.total > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {job.expectedSessions.processed} / {job.expectedSessions.total} ({expectedProgress}%)
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={job?.currentPhase === 'FINALIZING_EXPECTED_SESSIONS' ? expectedProgress : (job?.status === 'COMPLETED' || job?.currentPhase === 'CALCULATING_FEES' ? 100 : 0)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                }}
              />
            </Box>

            {/* PHASE 2 */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color={job?.currentPhase === 'CALCULATING_FEES' ? 'primary.main' : 'text.primary'}
                >
                  Phase 2: Calculate Forecast Fees
                </Typography>
                {job?.feeCalculation?.total > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {job.feeCalculation.processed} / {job.feeCalculation.total} ({feeProgress}%)
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={job?.currentPhase === 'CALCULATING_FEES' ? feeProgress : (job?.status === 'COMPLETED' ? 100 : 0)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                }}
              />
            </Box>

            {/* PHASE 3 */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color={isManualRunning ? 'primary.main' : 'text.primary'}
                >
                  Phase 3: Manual Forecast Sync
                </Typography>
                {manualJob?.total > 0 && (isManualRunning || manualJob?.status === 'COMPLETED') && (
                  <Typography variant="caption" color="text.secondary">
                    {manualJob.processed} / {manualJob.total} ({manualProgress}%)
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={manualJob?.status === 'COMPLETED' ? 100 : (isManualRunning ? manualProgress : 0)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                }}
              />
            </Box>
          </Paper>

          {manualJob?.status === 'COMPLETED' && (
            <Alert severity="success" icon={<Icon icon="mdi:check-circle" />}>
              Calculation sync completed successfully. Updated all expected sessions and forecast fee logs.
            </Alert>
          )}

          {(job?.status === 'FAILED' || manualJob?.status === 'FAILED') && (
            <Alert severity="error">
              Process failed.
              {job.status === 'FAILED' && job.errorList && job.errorList.length > 0 && (
                <Box sx={{ maxHeight: 80, overflowY: 'auto', mt: 1, fontSize: '0.75rem' }}>
                  {job.errorList.map((err, idx) => (
                    <div key={idx}>• {err}</div>
                  ))}
                </Box>
              )}
              {manualJob.status === 'FAILED' && manualJob.errorList && manualJob.errorList.length > 0 && (
                <Box sx={{ maxHeight: 80, overflowY: 'auto', mt: 1, fontSize: '0.75rem' }}>
                  {manualJob.errorList.map((err, idx) => (
                    <div key={idx}>• {err}</div>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </Box>
      }
    />
  );
};

export default ManualTriggers;
